import { useState, useRef, useEffect, useCallback } from "react";
import { marked } from "marked";
import "./AgentPage.css";
import { agentHealth } from "./api";

/**
 * AgentPage - Job Matching Agent
 *
 * Chat interface ported from Minerva_intelligence_UI_prototype 1.html's
 * #view-agent. The prototype was fully mocked; this version proxies each
 * message to the real IRIS chat engine via New-Minerva's Flask
 * (POST /api/agent/message, Server-Sent Events) and renders the streamed
 * reply live. All matching logic lives in IRIS - this page only relays and
 * displays.
 *
 * SSE frames from IRIS (passed straight through by the proxy):
 *   {type:"delta", text}                    incremental markdown
 *   {type:"tool_status", tool, input}       agent called a retrieval tool
 *   {type:"tool_result", tool, data}        structured match payload (added
 *                                           to IRIS on feat/minerva-tool-result-event)
 *   {type:"done", chat_id, reply}
 *   {type:"error", message}
 */

const STARTERS = [
  { label: "Match an engineering role", q: "engineer developing LLM-powered solutions" },
  {
    label: "Match a finance role",
    q: "Supports the finance manager with month-end close, budgeting and reporting; supervises junior finance staff",
  },
  { label: "Explain a job code", q: "What does the F-CORE-4 code cover?" },
  { label: "How tiers work", q: "How do experience tiers work?" },
];

const SOURCE_LABELS = {
  PayWell: "PwC PayWell",
  JobDescriptions: "Aon Radford",
  REM: "REMchannel",
  WTW: "WTW",
  Minerva: "Potentiam catalog",
};

marked.setOptions({ breaks: true, gfm: true });

// Minimal allow-list sanitizer for the markdown IRIS/Claude produces. Parses
// into a detached template, drops any element not on the list (keeping its
// text), and strips every attribute except href on <a> (forced safe).
const ALLOWED_TAGS = new Set(["P", "BR", "STRONG", "EM", "B", "I", "CODE", "PRE", "UL", "OL", "LI", "A", "H3", "H4", "BLOCKQUOTE", "SPAN"]);
function sanitize(html) {
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  const walk = (node) => {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === 1) {
        if (!ALLOWED_TAGS.has(child.tagName)) {
          const text = document.createTextNode(child.textContent || "");
          child.replaceWith(text);
          return;
        }
        [...child.attributes].forEach((attr) => {
          if (child.tagName === "A" && attr.name === "href" && /^https?:\/\//i.test(attr.value)) return;
          child.removeAttribute(attr.name);
        });
        if (child.tagName === "A") {
          child.setAttribute("target", "_blank");
          child.setAttribute("rel", "noopener noreferrer");
        }
        walk(child);
      } else if (child.nodeType !== 3) {
        child.remove();
      }
    });
  };
  walk(tpl.content);
  return tpl.innerHTML;
}

// Pull an optional [[QUICK_REPLIES]] a | b | c [[/QUICK_REPLIES]] block out of
// the accumulated text (IRIS convention - see its chat.html). Returns the text
// with the block removed and the parsed reply list.
function extractQuickReplies(text) {
  const m = text.match(/\[\[QUICK_REPLIES\]\]([\s\S]*?)\[\[\/QUICK_REPLIES\]\]/);
  if (!m) return { text, quickReplies: [] };
  const quickReplies = m[1]
    .split(/\||\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  return { text: text.replace(m[0], "").trim(), quickReplies };
}

function fitPercent(rec) {
  const c = rec.cosine != null ? Number(rec.cosine) * 100 : rec.score != null ? Number(rec.score) : null;
  if (c == null || Number.isNaN(c)) return null;
  return Math.max(0, Math.min(100, Math.round(c)));
}

function whyText(rec) {
  const bits = [];
  if (rec.stream_match === false || rec.market_stream) {
    if (rec.market_stream === "MGR") bits.push("manager level");
    else if (rec.market_stream === "IC") bits.push("individual contributor");
    else if (rec.market_stream === "EXEC") bits.push("executive scope");
  }
  if (typeof rec.level_distance === "number") {
    bits.push(rec.level_distance === 0 ? "level-aligned" : rec.level_distance < 0 ? "one level below" : "one level above");
  }
  if (rec.family_gated) bits.push("different job family");
  if (rec.match_logic && typeof rec.match_logic === "string" && bits.length === 0) bits.push(rec.match_logic);
  return bits.join(" · ");
}

// Normalise any tool_result payload into a flat list of match rows.
function extractMatches(evt) {
  const d = evt.data;
  if (!d) return { matches: [], outOfScope: false };
  if (evt.tool === "finalize_match" && d.chosen_market_match) {
    const c = d.chosen_market_match;
    return {
      matches: [
        {
          code: c.job_code,
          title: c.job_title,
          source: c.source,
          fitPct: null,
          why: d.internal_role_summary || "",
          final: true,
        },
      ],
      outOfScope: false,
    };
  }
  if (d.out_of_scope) return { matches: [], outOfScope: true };
  let rows = [];
  if (Array.isArray(d)) rows = d;
  else if (Array.isArray(d.results)) rows = d.results;
  else if (Array.isArray(d.matches)) rows = d.matches;
  else if (Array.isArray(d.candidates)) rows = d.candidates;
  if (d.closest_internal && !rows.some((r) => r.job_code === d.closest_internal.job_code)) {
    rows = [d.closest_internal, ...rows];
  }
  const matches = rows
    .filter((r) => r && r.job_code)
    .map((r) => ({
      code: r.job_code,
      title: r.job_title || r.title || r.job_code,
      source: r.source,
      fitPct: fitPercent(r),
      why: whyText(r) || (r.job_description ? String(r.job_description).slice(0, 140) : ""),
      final: false,
    }));
  return { matches, outOfScope: false };
}

function mergeMatches(existing, incoming) {
  const byCode = new Map(existing.map((m) => [m.code, m]));
  incoming.forEach((m) => {
    if (m.final) byCode.set(m.code, m);
    else if (!byCode.has(m.code)) byCode.set(m.code, m);
  });
  return [...byCode.values()].slice(0, 6);
}

function RobotIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <rect x="4" y="8" width="16" height="12" rx="3" />
      <path d="M12 8V4" />
      <circle cx="12" cy="3" r="1.4" />
      <circle cx="9" cy="14" r="1.1" />
      <circle cx="15" cy="14" r="1.1" />
    </svg>
  );
}

function MatchCard({ m, rank }) {
  return (
    <div className={`ag-match${m.final ? " ag-match-final" : ""}`}>
      <div className="ag-match-top">
        <span className={`ag-match-rank${m.final ? " ag-star" : ""}`}>{m.final ? "★" : rank}</span>
        <h4>{m.title}</h4>
        {m.code && <span className="ag-code-chip">{m.code}</span>}
        {m.fitPct != null && (
          <span className="ag-fit">
            <span className="ag-bar">
              <span style={{ width: `${m.fitPct}%` }} />
            </span>
            {m.fitPct}%
          </span>
        )}
      </div>
      {m.source && <p style={{ fontSize: 12, color: "var(--ag-ink-mute)", margin: "0 0 2px" }}>{SOURCE_LABELS[m.source] || m.source}</p>}
      {m.why && (
        <div className="ag-why">
          <b>{m.final ? "Recommended" : "Why this fits"}:</b> {m.why}
        </div>
      )}
    </div>
  );
}

function BotBubble({ msg }) {
  const showTyping = msg.streaming && !msg.text;
  return (
    <div className="ag-msg bot">
      <div className="ag-av">M</div>
      <div style={{ maxWidth: "88%" }}>
        <div className={`ag-bubble${msg.error ? " ag-error" : ""}`}>
          {showTyping ? (
            <div className="ag-typing">
              <i />
              <i />
              <i />
            </div>
          ) : msg.seed ? (
            msg.seedContent
          ) : (
            <span dangerouslySetInnerHTML={{ __html: sanitize(marked.parse(msg.text || "")) }} />
          )}
        </div>
        {msg.toolStatus && <div className="ag-tool-status">{msg.toolStatus}</div>}
        {msg.matches && msg.matches.length > 0 && (
          <div>
            {msg.matches.map((m, i) => (
              <MatchCard key={m.code || i} m={m} rank={i + 1} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const TOOL_LABELS = {
  search_market_jobs: "Searching the Potentiam job catalog…",
  match_internal_role: "Resolving the internal Potentiam role…",
  assess_role_level: "Checking the level ladder…",
  get_dataset_info: "Checking available data sources…",
  finalize_match: "Finalising the recommendation…",
};

export default function AgentPage() {
  const seedMessage = {
    role: "bot",
    seed: true,
    seedContent: (
      <>
        Hello &mdash; I&apos;m the Minerva Job Matching Agent. I&apos;ll help you map your roles to the right internal Potentiam job code (the 446-role catalog). Here&apos;s how I can help:
        <ul>
          <li>
            <strong>Match job descriptions</strong> to internal Potentiam job codes
          </li>
          <li>
            <strong>Explain the matching rationale</strong> behind each recommendation
          </li>
          <li>
            <strong>Compare close matches</strong> so you can pick the best fit
          </li>
          <li>
            <strong>Answer questions</strong> about the Potentiam job architecture
          </li>
        </ul>
        Pick a starting point below, or just tell me about the role you&apos;d like to match.
      </>
    ),
  };

  const [messages, setMessages] = useState([seedMessage]);
  const [sessionId, setSessionId] = useState(null);
  const [draft, setDraft] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [offline, setOffline] = useState(false);
  const [starterChips, setStarterChips] = useState(STARTERS.map((s) => s.label));

  const chatRef = useRef(null);
  const taRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    agentHealth()
      .then((h) => !cancelled && setOffline(!h || h.status === "down"))
      .catch(() => !cancelled && setOffline(true));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  const autosize = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
  }, []);

  const send = useCallback(
    async (text) => {
      const message = (text ?? "").trim();
      if (!message || streaming) return;

      setDraft("");
      requestAnimationFrame(autosize);
      const botIndex = messages.length + 1;
      setMessages((prev) => [
        ...prev,
        { role: "user", text: message },
        { role: "bot", text: "", matches: [], streaming: true },
      ]);
      setStreaming(true);

      const patchBot = (patch) =>
        setMessages((prev) => {
          const next = [...prev];
          const cur = next[botIndex];
          if (!cur) return prev;
          next[botIndex] = typeof patch === "function" ? patch(cur) : { ...cur, ...patch };
          return next;
        });

      try {
        const res = await fetch("/api/agent/message", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message, chat_id: sessionId }),
        });
        if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let acc = "";

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop() || "";
          for (const part of parts) {
            const line = part.trim();
            if (!line.startsWith("data:")) continue;
            let evt;
            try {
              evt = JSON.parse(line.slice(5).trim());
            } catch {
              continue;
            }
            if (evt.type === "delta") {
              acc += evt.text || "";
              const { text: cleanText } = extractQuickReplies(acc);
              patchBot({ text: cleanText, toolStatus: null });
            } else if (evt.type === "tool_status") {
              patchBot({ toolStatus: TOOL_LABELS[evt.tool] || "Working…" });
            } else if (evt.type === "tool_result") {
              const { matches, outOfScope } = extractMatches(evt);
              if (matches.length) patchBot((cur) => ({ ...cur, matches: mergeMatches(cur.matches || [], matches) }));
              if (outOfScope) patchBot((cur) => ({ ...cur, matches: [] }));
            } else if (evt.type === "done") {
              if (evt.chat_id) setSessionId(evt.chat_id);
              const finalText = extractQuickReplies(acc);
              patchBot({ text: finalText.text, streaming: false, toolStatus: null });
              if (finalText.quickReplies.length) setStarterChips(finalText.quickReplies);
            } else if (evt.type === "error") {
              patchBot({ text: evt.message || "Something went wrong generating a reply.", error: true, streaming: false, toolStatus: null });
            }
          }
        }
        patchBot((cur) => (cur.streaming ? { ...cur, streaming: false } : cur));
      } catch (err) {
        patchBot({
          text: "Job Matching Agent is unavailable. Check that the IRIS service is running, then try again.",
          error: true,
          streaming: false,
          toolStatus: null,
        });
      } finally {
        setStreaming(false);
      }
    },
    [messages.length, sessionId, streaming, autosize]
  );

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(draft);
    }
  };

  return (
    <div className="ag-page">
      <div className="ag-eyebrow">Minerva Intelligence &middot; AI Agent</div>
      <h1 className="ag-title">
        Job Matching <em>Agent</em>
      </h1>
      <p className="ag-lede">
        Describe a position in your own words. The agent maps it to the right internal Potentiam job code &mdash; from the 446-role catalog &mdash; explains why, and lets you compare the close calls, so matching is consistent across everyone who uses it.
      </p>

      <div className="ag-export">
        <span className="ag-lbl">Export</span>
        <button type="button" className="ag-exp-btn" disabled title="Export not available yet">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
          </svg>
          <span className="ag-fmt">PDF</span>
        </button>
        <button type="button" className="ag-exp-btn" disabled title="Export not available yet">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 9h18M9 3v18" />
          </svg>
          <span className="ag-fmt">XLS</span>
        </button>
      </div>

      <div className="ag-wrap">
        <div className="ag-card">
          <div className="ag-head">
            <div className="ag-icon">
              <RobotIcon />
            </div>
            <div>
              <h2>Minerva Job Matching Agent</h2>
              <div className="ag-sub">AI-powered compensation job matching assistant</div>
            </div>
            <span className="ag-beta">BETA</span>
            {offline && <span className="ag-offline">SERVICE OFFLINE</span>}
          </div>

          <div className="ag-chat" ref={chatRef}>
            {messages.map((msg, i) =>
              msg.role === "user" ? (
                <div className="ag-msg user" key={i}>
                  <div className="ag-av">IS</div>
                  <div className="ag-bubble">{msg.text}</div>
                </div>
              ) : (
                <div key={i}>
                  <BotBubble msg={msg} />
                  {msg.seed && (
                    <div className="ag-chips" style={{ marginLeft: 43 }}>
                      {STARTERS.map((s) => (
                        <button key={s.label} type="button" className="ag-chip" disabled={streaming} onClick={() => send(s.q)}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            )}
            {!streaming && starterChips.length > 0 && messages.length > 1 && (
              <div className="ag-chips">
                {starterChips.map((c) => (
                  <button key={c} type="button" className="ag-chip" onClick={() => send(c)}>
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="ag-composer">
            <textarea
              ref={taRef}
              rows={1}
              placeholder={"Describe a role, e.g. “engineer developing LLM-powered solutions”…"}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                autosize();
              }}
              onKeyDown={onKeyDown}
              disabled={streaming}
            />
            <button type="button" className="ag-send" onClick={() => send(draft)} disabled={streaming || !draft.trim()} aria-label="Send">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
        <div className="ag-foot">
          <span>Matches are suggestions &mdash; always confirm against the live Potentiam job architecture.</span>
          <span className="ag-mono">model &middot; minerva-match</span>
        </div>
      </div>
    </div>
  );
}
